import {BaseLessonFragment, BaseLessonFragmentProps, BaseLessonFragmentState} from "../BaseLessonFragment";
import React from "react";
import {StyledBoxColumn} from "../../../shared/StyledComponents";
import {PD_LG, PD_XLG} from "../../../shared/dimens";
import {Agent} from "../../Agent";
import {LessonId} from "../types";
import {App} from "../../App";
import {ChatPersonaType} from "../../../toktiv-common/types";

export type ScenarioFragmentProps = BaseLessonFragmentProps & {}

export type ScenarioFragmentState = BaseLessonFragmentState & {
  pages: ScenarioPage[],
  encodedFiles: string[], // base64 encoded mp3
  fetchingFiles: boolean[],
}

export type ScenarioPage = {
  text: string,
}

const PAGES = new Map<string, ScenarioPage[]>([
  [LessonId.SCENARIO_ORDERING_COFFEE, [
    {
      text: "Hello, welcome to the Coffee Shop. How are you doing today?",
    },
    {
      text: "Great. What would you like to order?",
    },
    {
      text: "Would you like to add sugar and milk?",
    },
  ]],
]);

export class ScenarioFragment extends BaseLessonFragment<ScenarioFragmentProps, ScenarioFragmentState> {

  private readonly agent = new Agent();

  static totalPages(): number {
    return 10;
  }

  componentDidUpdate(prevProps: Readonly<ScenarioFragmentProps>, prevState: Readonly<ScenarioFragmentState>, snapshot?: any) {
    super.componentDidUpdate(prevProps, prevState, snapshot);
    const audio = App.getAppRefs().audio;
    const index = this.props.pageIndex;
    const encodedFile = this.state.encodedFiles[index];
    if (index !== prevProps.pageIndex) {
      this.fetchEncodedFiles();
      audio.pause();
      if (encodedFile) {
        this.play(audio, encodedFile);
      }
    } else if (encodedFile && !prevState.encodedFiles[index]) {
      this.play(audio, encodedFile);
    }
  }

  private play(audio: HTMLAudioElement, encodedFile: string): void {
    audio.src = "data:audio/mp3;base64," + encodedFile;
    audio.play();
  }

  private async maybeFetchEncodedFiles(index: number): Promise<void> {
    if (!this.state.encodedFiles[index] && !this.state.fetchingFiles[index]) {
      const fetchingFiles = [...this.state.fetchingFiles];
      fetchingFiles[index] = true;
      this.setState({
        fetchingFiles,
      });
      const question = this.state.pages[index].text;
      const encodedFiles = [...this.state.encodedFiles];
      encodedFiles[index] = await this.agent.textToSpeech(ChatPersonaType.ARYA, question, this.props.lesson.id + "-" + index);
      this.setState({
        encodedFiles,
      });
    }
  }

  private async fetchEncodedFiles(): Promise<void> {
    const index = this.props.pageIndex;
    await this.maybeFetchEncodedFiles(index);
    await this.maybeFetchEncodedFiles(index + 1);
  }

  protected onCreateState(): ScenarioFragmentState {
    const totalPages = ScenarioFragment.totalPages();
    return {
      ...super.onCreateState(),
      encodedFiles: new Array<string>(totalPages),
      fetchingFiles: new Array<boolean>(totalPages),
    };
  }

  protected containerContentDidRender() {
    super.containerContentDidRender();
    this.fetchEncodedFiles();
  }

  protected renderContainerContent(): React.ReactElement | null {
    const pageIndex = this.props.pageIndex;
    const question = this.state.pages[pageIndex].text;
    return <StyledBoxColumn style={{flexGrow: 1, padding: PD_LG, gap: PD_XLG}}>
      {this.renderQuestion(question)}
    </StyledBoxColumn>;
  }

  protected async fetchOnMount(forceReload?: boolean): Promise<any> {
    this.setState({
      pages: await this.genPages(),
    });
  }

  protected async genPages(): Promise<ScenarioPage[]> {
    return PAGES.get(this.props.lesson.id);
  }
}